import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import {
  PlayArrow as GenerateIcon,
  Refresh as RegenerateIcon,
  ExpandMore as ExpandMoreIcon,
  Download as DownloadIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Description as FileIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useApiClient } from '../../config/axios';

interface GenerationStats {
  models: number;
  fields: number;
  lastGenerated: Date | null;
}

interface NavTreeView {
  view: string;
  unique_id: string;
  model_count: number;
  view_count: number;
  model_source: string;
}

interface NavTreeLeftNav {
  left_nav: string;
  views: NavTreeView[];
}

interface NavTreeNode {
  top_nav: string;
  left_navs: NavTreeLeftNav[];
}

interface DerivedModel {
  id: string;
  template_model_id: string;
  unique_id: string;
  created_at: string;
  field_count: number;
}

interface DerivedModelDetail {
  id: string;
  template_model_id: string;
  unique_id: string;
  historyflag: boolean;
  pivotdefn: string;
  contentdimensionid: string;
  model: any[];
  fields: {
    name: string;
    type: string;
    defnid: string;
    defntype: string;
    behaviorparams: any;
    name_full: string;
    attr_prefix: string;
    attr: string;
    attr_suffix: string;
    source_view: string;
  }[];
}

const ModelManager: React.FC = () => {
  const apiClient = useApiClient();
  const [generationStats, setGenerationStats] = useState<GenerationStats>({
    models: 0,
    fields: 0,
    lastGenerated: null,
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Navigation tree state
  const [navTree, setNavTree] = useState<NavTreeNode[]>([]);
  const [expandedTopNav, setExpandedTopNav] = useState<Set<string>>(new Set());
  const [expandedLeftNav, setExpandedLeftNav] = useState<Set<string>>(new Set());
  const [selectedView, setSelectedView] = useState<{
    topNav: string;
    leftNav: string;
    view: string;
  } | null>(null);

  // Models for selected view
  const [modelsForView, setModelsForView] = useState<DerivedModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);

  // Model detail dialog
  const [selectedModel, setSelectedModel] = useState<DerivedModelDetail | null>(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [loadingModelDetail, setLoadingModelDetail] = useState(false);

  // Model comparison dialog
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editedModel, setEditedModel] = useState<DerivedModelDetail | null>(null);
  const [saving, setSaving] = useState(false);

  // OCI sync state
  const [ociAvailable, setOciAvailable] = useState(false);
  const [ociSyncing, setOciSyncing] = useState(false);

  const fetchNavigationTree = useMemo(() => async () => {
    try {
      const response = await apiClient.get('/navigation-tree');
      setNavTree(response.data.tree || []);
    } catch (err) {
      console.error('Failed to fetch navigation tree:', err);
    }
  }, [apiClient]);

  // Load navigation tree on mount
  useEffect(() => {
    fetchNavigationTree();
  }, [fetchNavigationTree]);

  // Check OCI availability on mount
  useEffect(() => {
    const checkOCI = async () => {
      try {
        const response = await apiClient.get('/derived-models/oci/check');
        setOciAvailable(response.data.available);
      } catch (error) {
        console.error('Failed to check OCI availability:', error);
        setOciAvailable(false);
      }
    };
    checkOCI();
  }, [apiClient]);

  // Fetch generation stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await apiClient.get('/derived-models/stats');
        if (response.data.lastGenerated) {
          setGenerationStats({
            models: response.data.models,
            fields: response.data.fields,
            lastGenerated: new Date(response.data.lastGenerated),
          });
        }
      } catch (error) {
        console.error('Failed to fetch generation stats:', error);
      }
    };
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerateModels = async () => {
    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.post('/generate-models', {
        created_by: 'user'
      });

      if (response.data.success) {
        setGenerationStats({
          models: response.data.models_generated,
          fields: response.data.fields_generated,
          lastGenerated: new Date(),
        });
        setSuccess(
          `Successfully generated ${response.data.models_generated} models with ${response.data.fields_generated} fields`
        );
        // Refresh navigation tree
        await fetchNavigationTree();
      } else {
        setError(response.data.error || 'Failed to generate models');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate models');
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerateModels = async () => {
    if (!window.confirm('This will delete all derived models and regenerate them. Continue?')) {
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    try {
      // Delete all
      await apiClient.delete('/derived-models');

      // Generate new
      await handleGenerateModels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate models');
      setGenerating(false);
    }
  };

  const toggleTopNav = (topNav: string) => {
    const newExpanded = new Set(expandedTopNav);
    if (newExpanded.has(topNav)) {
      newExpanded.delete(topNav);
    } else {
      newExpanded.add(topNav);
    }
    setExpandedTopNav(newExpanded);
  };

  const toggleLeftNav = (key: string) => {
    const newExpanded = new Set(expandedLeftNav);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedLeftNav(newExpanded);
  };

  const handleViewSelect = async (topNav: string, leftNav: string, view: string) => {
    setSelectedView({ topNav, leftNav, view });
    setLoadingModels(true);

    try {
      const response = await apiClient.get(`/navigation/${encodeURIComponent(topNav)}/${encodeURIComponent(leftNav)}/${encodeURIComponent(view)}/models`);
      setModelsForView(response.data.models || []);
    } catch (err) {
      console.error('Failed to fetch models:', err);
      setModelsForView([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleViewModelJson = async (modelId: string) => {
    setLoadingModelDetail(true);
    setJsonDialogOpen(true);

    try {
      const response = await apiClient.get(`/derived-models/${encodeURIComponent(modelId)}`);
      setSelectedModel(response.data);
    } catch (err) {
      console.error('Failed to fetch model details:', err);
    } finally {
      setLoadingModelDetail(false);
    }
  };

  const handleCompareModel = async (modelId: string) => {
    setLoadingComparison(true);
    setCompareDialogOpen(true);

    try {
      const response = await apiClient.get(`/derived-models/${encodeURIComponent(modelId)}/compare`);
      setComparisonData(response.data);
    } catch (err) {
      console.error('Failed to fetch comparison:', err);
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleEditModel = () => {
    if (!selectedModel) return;
    setEditedModel(JSON.parse(JSON.stringify(selectedModel))); // Deep copy
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedModel(null);
  };

  const handleSaveModel = async () => {
    if (!editedModel) return;

    setSaving(true);
    setError(null);

    try {
      // Save model metadata
      await apiClient.put(`/derived-models/${editedModel.id}`, {
        historyflag: editedModel.historyflag,
        pivotdefn: editedModel.pivotdefn,
        contentdimensionid: editedModel.contentdimensionid,
        updated_by: 'user'
      });

      // Refresh model details
      const response = await apiClient.get(`/derived-models/${encodeURIComponent(editedModel.id)}`);
      setSelectedModel(response.data);
      setSuccess('Model updated successfully');
      setEditMode(false);
      setEditedModel(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save model');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateField = async (fieldName: string, updates: Partial<any>) => {
    if (!editedModel) return;

    try {
      await apiClient.put(`/derived-models/${editedModel.id}/fields/${fieldName}`, updates);

      // Update local state
      setEditedModel({
        ...editedModel,
        fields: editedModel.fields.map(f =>
          f.name === fieldName ? { ...f, ...updates } : f
        )
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update field');
    }
  };

  const handleDeleteField = async (fieldName: string) => {
    if (!editedModel) return;
    if (!window.confirm(`Delete field "${fieldName}"?`)) return;

    try {
      await apiClient.delete(`/derived-models/${editedModel.id}/fields/${fieldName}`);

      // Update local state
      setEditedModel({
        ...editedModel,
        fields: editedModel.fields.filter(f => f.name !== fieldName)
      });
      setSuccess(`Field "${fieldName}" deleted`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete field');
    }
  };

  const handleAddField = async () => {
    if (!editedModel) return;

    const name = prompt('Enter field name:');
    if (!name) return;

    const type = prompt('Enter field type (e.g., string, number):');
    if (!type) return;

    try {
      const response = await apiClient.post(`/derived-models/${editedModel.id}/fields`, {
        name,
        type,
        defnid: '',
        defntype: '',
        name_full: name,
        attr: name,
        source_view: ''
      });

      // Update local state
      setEditedModel({
        ...editedModel,
        fields: [...editedModel.fields, response.data.field]
      });
      setSuccess(`Field "${name}" added`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add field');
    }
  };

  const handleExportJson = () => {
    if (!selectedModel) return;

    const exportData = {
      [selectedModel.id]: {
        id: selectedModel.id,
        historyFlag: selectedModel.historyflag,
        pivotDefn: selectedModel.pivotdefn,
        contentDimensionId: selectedModel.contentdimensionid,
        model: selectedModel.model,
      },
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedModel.id}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleSyncToOCI = async () => {
    if (!selectedModel) return;

    if (!window.confirm('This will sync the model to OCI. Continue?')) {
      return;
    }

    try {
      setOciSyncing(true);

      const modelJson = {
        [selectedModel.id]: {
          id: selectedModel.id,
          historyFlag: selectedModel.historyflag,
          pivotDefn: selectedModel.pivotdefn,
          contentDimensionId: selectedModel.contentdimensionid,
          model: selectedModel.model,
        },
      };

      const response = await apiClient.post(`/derived-models/${encodeURIComponent(selectedModel.id)}/sync-to-oci`, modelJson);

      if (response.data.success) {
        alert('Model synced to OCI successfully!');
      } else {
        throw new Error(response.data.error || 'Sync failed');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to sync to OCI';
      alert(`Sync failed: ${errorMessage}`);
      console.error('OCI sync error:', err);
    } finally {
      setOciSyncing(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Model Manager
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Generate and manage derived models from templates and view attributes
        </Typography>
      </Box>

      {/* Error/Success Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Generation Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Model Generation</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <GenerateIcon />}
              onClick={handleGenerateModels}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Generate Models'}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<RegenerateIcon />}
              onClick={handleRegenerateModels}
              disabled={generating}
            >
              Regenerate All
            </Button>
          </Box>
        </Box>

        {/* Statistics */}
        {generationStats.lastGenerated && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <Chip label={`${generationStats.models} Models`} color="primary" />
            <Chip label={`${generationStats.fields} Fields`} color="secondary" />
            <Chip label={`Last Generated: ${generationStats.lastGenerated.toLocaleString()}`} />
          </Box>
        )}
      </Paper>

      {/* Navigation Tree & Models */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left: Navigation Tree */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 33%' } }}>
          <Paper sx={{ p: 2, maxHeight: '70vh', overflow: 'auto' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Navigation
            </Typography>

            {navTree.map((topNavNode) => (
              <Box key={topNavNode.top_nav} sx={{ mb: 1 }}>
                <Accordion
                  expanded={expandedTopNav.has(topNavNode.top_nav)}
                  onChange={() => toggleTopNav(topNavNode.top_nav)}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {expandedTopNav.has(topNavNode.top_nav) ? <FolderOpenIcon /> : <FolderIcon />}
                      <Typography>{topNavNode.top_nav}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pl: 2 }}>
                    {topNavNode.left_navs.map((leftNavNode) => {
                      const leftNavKey = `${topNavNode.top_nav}:${leftNavNode.left_nav}`;
                      return (
                        <Box key={leftNavKey} sx={{ mb: 1 }}>
                          <Accordion
                            expanded={expandedLeftNav.has(leftNavKey)}
                            onChange={() => toggleLeftNav(leftNavKey)}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {expandedLeftNav.has(leftNavKey) ? <FolderOpenIcon fontSize="small" /> : <FolderIcon fontSize="small" />}
                                <Typography variant="body2">{leftNavNode.left_nav}</Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pl: 2 }}>
                              {leftNavNode.views.map((viewNode) => (
                                <Box
                                  key={viewNode.view}
                                  sx={{
                                    p: 1,
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    backgroundColor:
                                      selectedView?.topNav === topNavNode.top_nav &&
                                        selectedView?.leftNav === leftNavNode.left_nav &&
                                        selectedView?.view === viewNode.view
                                        ? 'action.selected'
                                        : 'transparent',
                                    '&:hover': { backgroundColor: 'action.hover' },
                                  }}
                                  onClick={() =>
                                    handleViewSelect(topNavNode.top_nav, leftNavNode.left_nav, viewNode.view)
                                  }
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FileIcon fontSize="small" />
                                    <Typography variant="body2">{viewNode.view}</Typography>
                                    <Chip label={viewNode.model_count} size="small" color="primary" />
                                  </Box>
                                </Box>
                              ))}
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              </Box>
            ))}
          </Paper>
        </Box>

        {/* Right: Model Cards */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 67%' } }}>
          <Paper sx={{ p: 3, minHeight: '70vh' }}>
            {selectedView ? (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Models for {selectedView.topNav} / {selectedView.leftNav} / {selectedView.view}
                </Typography>

                {loadingModels ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : modelsForView.length === 0 ? (
                  <Alert severity="info">No derived models found for this view</Alert>
                ) : (
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                    {modelsForView.map((model) => (
                      <Card key={model.id} variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Model Name
                          </Typography>
                          <Typography
                            sx={{
                              mb: 2,
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              wordBreak: 'break-word'
                            }}
                            title={model.id}
                          >
                            {model.id}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip label={`Template: ${model.template_model_id}`} size="small" variant="outlined" />
                            <Chip label={`${model.field_count} fields`} size="small" color="primary" />
                          </Box>
                        </CardContent>
                        <CardActions>
                          <Button size="small" onClick={() => handleViewModelJson(model.id)}>
                            View Model
                          </Button>
                          <Button size="small" onClick={() => handleCompareModel(model.id)}>
                            Compare
                          </Button>
                        </CardActions>
                      </Card>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Typography variant="body1" color="text.secondary">
                  Select a view from the navigation tree to see its models
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Model Detail Dialog */}
      <Dialog open={jsonDialogOpen} onClose={() => { setJsonDialogOpen(false); setEditMode(false); }} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{editMode ? 'Edit' : 'View'} Model: {selectedModel?.id}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {!editMode && (
                <>
                  <Button startIcon={<EditIcon />} onClick={handleEditModel} disabled={!selectedModel}>
                    Edit
                  </Button>
                  <Button startIcon={<DownloadIcon />} onClick={handleExportJson} disabled={!selectedModel}>
                    Export Model
                  </Button>
                  <Button variant="contained" onClick={handleSyncToOCI} disabled={!selectedModel || ociSyncing || !ociAvailable}>
                    {ociSyncing ? 'Syncing...' : 'Sync to OCI'}
                  </Button>
                </>
              )}
              {editMode && (
                <>
                  <Button startIcon={<SaveIcon />} onClick={handleSaveModel} disabled={saving} variant="contained">
                    Save
                  </Button>
                  <Button startIcon={<CancelIcon />} onClick={handleCancelEdit} disabled={saving}>
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingModelDetail ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (editMode && editedModel) || selectedModel ? (
            <>
              {/* Metadata */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Metadata</strong>
                </Typography>
                {editMode && editedModel ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Template Model ID"
                      value={editedModel.template_model_id}
                      disabled
                      size="small"
                    />
                    <TextField
                      label="Unique ID"
                      value={editedModel.unique_id}
                      disabled
                      size="small"
                    />
                    <TextField
                      label="Pivot Definition"
                      value={editedModel.pivotdefn || ''}
                      onChange={(e) => setEditedModel({ ...editedModel, pivotdefn: e.target.value })}
                      size="small"
                    />
                    <TextField
                      label="Content Dimension ID"
                      value={editedModel.contentdimensionid || ''}
                      onChange={(e) => setEditedModel({ ...editedModel, contentdimensionid: e.target.value })}
                      size="small"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editedModel.historyflag || false}
                          onChange={(e) => setEditedModel({ ...editedModel, historyflag: e.target.checked })}
                        />
                      }
                      label="History Flag"
                    />
                  </Box>
                ) : selectedModel ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label={`Template: ${selectedModel.template_model_id}`} />
                    <Chip label={`Unique ID: ${selectedModel.unique_id}`} />
                    <Chip label={`Pivot: ${selectedModel.pivotdefn}`} />
                    <Chip label={`Content Dimension: ${selectedModel.contentdimensionid}`} />
                    <Chip label={`History Flag: ${selectedModel.historyflag}`} />
                  </Box>
                ) : null}
              </Box>

              {/* JSON */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  <strong>Model JSON</strong>
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: '#f5f5f5', maxHeight: '300px', overflow: 'auto' }}>
                  <pre style={{ margin: 0, fontSize: '12px' }}>
                    {JSON.stringify(
                      {
                        [selectedModel.id]: {
                          id: selectedModel.id,
                          historyFlag: selectedModel.historyflag,
                          pivotDefn: selectedModel.pivotdefn,
                          contentDimensionId: selectedModel.contentdimensionid,
                          model: selectedModel.model,
                        },
                      },
                      null,
                      2
                    )}
                  </pre>
                </Paper>
              </Box>

              {/* Fields Table */}
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1">
                    <strong>Fields ({editMode && editedModel ? editedModel.fields.length : selectedModel?.fields.length})</strong>
                  </Typography>
                  {editMode && (
                    <Button startIcon={<AddIcon />} onClick={handleAddField} size="small">
                      Add Field
                    </Button>
                  )}
                </Box>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Field Name</TableCell>
                        <TableCell>Prefix</TableCell>
                        <TableCell>Attr</TableCell>
                        <TableCell>Suffix</TableCell>
                        <TableCell>Type</TableCell>
                        {editMode && <TableCell align="right">Actions</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(editMode && editedModel ? editedModel.fields : selectedModel?.fields || []).map((field, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{field.name_full}</TableCell>
                          <TableCell>{field.attr_prefix || '-'}</TableCell>
                          <TableCell>{field.attr}</TableCell>
                          <TableCell>{field.attr_suffix || '-'}</TableCell>
                          <TableCell>{field.type}</TableCell>
                          {editMode && (
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteField(field.name)}
                                title="Delete field"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Model Comparison Dialog */}
      <Dialog open={compareDialogOpen} onClose={() => setCompareDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle>Model Comparison</DialogTitle>
        <DialogContent dividers>
          {loadingComparison ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : comparisonData ? (
            <>
              {/* Summary Stats */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Comparison Summary
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip label={`Template: ${comparisonData.template.fieldCount} fields`} color="default" />
                  <Chip label={`Generated: ${comparisonData.generated.fieldCount} fields`} color="primary" />
                  <Chip label={`Matched: ${comparisonData.comparison.matchedCount}`} color="success" />
                  <Chip label={`Only in Template: ${comparisonData.comparison.onlyInTemplateCount}`} color="error" />
                  <Chip label={`Only in Generated: ${comparisonData.comparison.onlyInGeneratedCount}`} color="warning" />
                </Box>
              </Box>

              {/* Side-by-Side Comparison */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                {/* Template Fields */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Template: {comparisonData.template.id}
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field Name</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparisonData.template.fields.map((field: any, idx: number) => {
                          const isMatched = comparisonData.comparison.matched.some((m: any) => m.name === field.name);
                          const isOnlyInTemplate = comparisonData.comparison.onlyInTemplate.some((m: any) => m.name === field.name);
                          return (
                            <TableRow
                              key={idx}
                              sx={{
                                backgroundColor: isOnlyInTemplate ? '#ffebee' : isMatched ? '#f1f8f4' : 'inherit'
                              }}
                            >
                              <TableCell>{field.name}</TableCell>
                              <TableCell>
                                {isOnlyInTemplate && <Chip label="Not in Generated" size="small" color="error" />}
                                {isMatched && <Chip label="Matched" size="small" color="success" />}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                {/* Generated Fields */}
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Generated: {comparisonData.generated.id}
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Field Name</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comparisonData.generated.fields.map((field: any, idx: number) => {
                          const isMatched = comparisonData.comparison.matched.some((m: any) => m.name === field.name);
                          const isOnlyInGenerated = comparisonData.comparison.onlyInGenerated.some((m: any) => m.name === field.name);
                          return (
                            <TableRow
                              key={idx}
                              sx={{
                                backgroundColor: isOnlyInGenerated ? '#fff3e0' : isMatched ? '#f1f8f4' : 'inherit'
                              }}
                            >
                              <TableCell>{field.name}</TableCell>
                              <TableCell>
                                {isOnlyInGenerated && <Chip label="Not in Template" size="small" color="warning" />}
                                {isMatched && <Chip label="Matched" size="small" color="success" />}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompareDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModelManager;